/**
 * Die vier Modi aus §8 als **Voreinstellungen** — §67 Schritt 5.
 *
 * **Vorschläge, keine Vorschriften.** `docs/GAME_RULES.md` Abschnitt 4 sagt es ausdrücklich: die
 * vier Zeilen sind Voreinstellungen und keine abgeschlossene Liste. Der Host darf Teams frei
 * bilden (Nr. 24, auch 2:1 oder 3:2), und die Startsektoren wählt er in **jeder** Spielerzahl
 * selbst (Nr. 8) — der Server erzwingt kein Muster. Diese Datei füllt deshalb nur ein Formular
 * vor, das danach vollständig editierbar bleibt.
 *
 * **Die Geometrie kommt aus der Engine.** Welcher Sektor welchem gegenüberliegt und welche
 * nebeneinander liegen, ist logische Boardstruktur (`SECTOR_RING`, `oppositeSector`, seit 10e).
 * Sie hier abzuschreiben wäre eine zweite Wahrheit über das Brett (`CLAUDE.md` Regel 9) — und
 * zwar eine, die in der Hälfte der Fälle zufällig stimmt, weil `SECTOR_IDS` zwar sechs Sektoren
 * aufzählt, aber nichts über ihre Lage sagt.
 *
 * **Was die Vorschläge begründet:**
 *
 * - **1:1 gegenüber.** §8.1 nennt das den Normalfall — und ausdrücklich keine Schranke (Nr. 8).
 * - **Gleichmäßig verteilen** bei 3, 4 und 6 Spielern, wie in `GAME_RULES.md` Abschnitt 12
 *   beschrieben. Bei 5 ist die Asymmetrie unvermeidlich; der Vorschlag versteckt das nicht.
 * - **Teams abwechselnd im Ring.** §9 verlangt, dass Mitglieder desselben Teams nicht direkt
 *   hintereinander ziehen; die dokumentierte Reihenfolge ist `A1 B1 A2 B2 A3 B3` beziehungsweise
 *   `A1 B1 C1 A2 B2 C2`. Abwechselnd verteilte Farben erzeugen genau das.
 * - **Teambezeichner sind Großbuchstaben**, nicht deutsche Wörter: sie werden gespeichert und
 *   wandern in Protokolle (Regel 6).
 */

import { SECTOR_RING, oppositeSector, type SectorId } from '@flaghex/engine';
import type { SlotConfigDto } from '@flaghex/contracts';

export type PresetId = 'duel' | 'freeForAll' | 'teams222' | 'teams33';

export interface ModePreset {
  readonly id: PresetId;
  /** Die Spielerzahlen, die dieser Modus zulässt — die erste ist der Vorschlag. */
  readonly playerCounts: readonly number[];
}

/** Die vier Modi in der Reihenfolge aus §42. */
export const MODE_PRESETS: readonly ModePreset[] = [
  { id: 'duel', playerCounts: [2] },
  // „Bei Jeder gegen Jeden zusätzlich Teilnehmerzahl" (§42).
  { id: 'freeForAll', playerCounts: [3, 4, 5, 6] },
  { id: 'teams222', playerCounts: [6] },
  { id: 'teams33', playerCounts: [6] },
];

const RING = SECTOR_RING.length;

/**
 * `count` Sektoren, so gleichmäßig über den Ring verteilt, wie es geht.
 *
 * Bei 2, 3 und 6 geht es auf; bei 4 ergeben sich zwei gegenüberliegende Paare; bei 5 bleibt
 * genau eine Lücke, und ihre beiden Nachbarn haben eine freie Flanke — unvermeidlich
 * (`GAME_RULES.md` Abschnitt 12).
 */
export function evenlySpacedSectors(count: number): readonly SectorId[] {
  if (!Number.isInteger(count) || count < 2 || count > RING) {
    throw new Error(`Keine mögliche Spielerzahl: ${count}`);
  }
  if (count === 2) {
    const first = SECTOR_RING[0] as SectorId;
    return [first, oppositeSector(first)];
  }
  if (count === 3) {
    return [0, 2, 4].map((index) => SECTOR_RING[index] as SectorId);
  }
  if (count === 4) {
    // Zwei gegenüberliegende Paare: 0 und 1, dazu ihre Gegenüber.
    return [0, 1, 3, 4].map((index) => SECTOR_RING[index] as SectorId);
  }
  // 5 und 6: der Reihe nach; bei 5 bleibt der letzte Sektor frei.
  return SECTOR_RING.slice(0, count);
}

/** Wie viele Teams ein Modus bildet — `undefined` heißt: jeder für sich. */
function teamCountOf(preset: PresetId): number | undefined {
  switch (preset) {
    case 'teams222':
      return 3;
    case 'teams33':
      return 2;
    case 'duel':
    case 'freeForAll':
      return undefined;
  }
}

/**
 * Die Farbplätze, die ein Modus vorschlägt.
 *
 * Die Teams werden **abwechselnd im Ring** vergeben (§9, siehe Modulkopf). Bei drei Teams fallen
 * die Partner damit einander gegenüber, bei zwei Teams wechseln sie sich rundum ab.
 */
export function presetSlots(preset: PresetId, playerCount: number): readonly SlotConfigDto[] {
  const sectors = evenlySpacedSectors(playerCount);
  const teams = teamCountOf(preset);
  if (teams === undefined) {
    return sectors.map((sector) => ({ sector }));
  }
  return sectors.map((sector, index) => ({
    sector,
    team: String.fromCharCode('A'.charCodeAt(0) + (index % teams)),
  }));
}
